///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { Sphere, Vector2 } from "three";

import { IComponent } from "@inweb/viewer-core";
import type { Viewer } from "../Viewer";

export class CameraComponent implements IComponent {
  protected viewer: Viewer;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.viewer.addEventListener("databasechunk", this.geometryEnd);
  }

  dispose() {
    this.viewer.removeEventListener("databasechunk", this.geometryEnd);
  }

  geometryEnd = () => {
    const extentsSize = this.viewer.extents.getBoundingSphere(new Sphere()).radius * 2;
    const rendererSize = this.viewer.renderer.getSize(new Vector2());
    const aspect = rendererSize.x / rendererSize.y;

    let camera: any;

    // TODO: do not change the camera and target after opening the second model in "append" mode
    this.viewer.scene.traverse((object: any) => {
      if (object.isCamera)
        if (!camera) camera = object;
        else if (object.isPerspectiveCamera && camera.isOrthographicCamera) camera = object;
    });

    if (camera) {
      camera.isDefaultCamera = true;
      camera.scale.set(1, 1, 1); // <- Visualize fix

      this.viewer.camera = camera;
      this.viewer.renderPass.camera = camera;
      this.viewer.helpersPass.camera = camera;
      this.viewer.ssaaRenderPass.camera = camera;
    } else {
      camera = this.viewer.camera;
    }

    if (camera.isPerspectiveCamera) {
      camera.aspect = aspect;
      camera.near = extentsSize / 1000;
      camera.far = extentsSize * 1000;
      camera.updateProjectionMatrix();
    }
    if (camera.isOrthographicCamera) {
      camera.left = camera.bottom * aspect;
      camera.right = camera.top * aspect;
      camera.near = 0;
      camera.far = extentsSize * 1000;
      camera.updateProjectionMatrix();
    }

    if (!camera.isDefaultCamera) {
      this.viewer.executeCommand("setDefaultViewPosition");
    }
  };
}
